import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Video {
  id: number;
  title: string;
  description?: string;
  youtube_url: string;
  thumbnail_url?: string;
  is_active: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

const VideoGallery: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchVideos();
  }, []);


  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/cms/videos');
      setVideos(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load videos');
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const extractYoutubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getEmbedUrl = (youtubeUrl: string): string | null => {
    const videoId = extractYoutubeId(youtubeUrl);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const getThumbnailUrl = (youtubeUrl: string, customThumbnail?: string): string | null => {
    if (customThumbnail) return customThumbnail;
    const videoId = extractYoutubeId(youtubeUrl);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const handleVideoClick = (video: Video) => {
    setSelectedVideo(video);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <div className="video-gallery-loading" style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <h3>{t('public.videos.loading')}</h3>
        <p>{t('public.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-gallery-error" style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '40px'
      }}>
        <h3>{t('public.videos.errorTitle')}</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchVideos}>
          {t('public.videos.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="video-gallery" style={{ 
      minHeight: '80vh',
      background: '#f8f9fa'
    }}>
      {/* Hero Section */}
      <div className="video-gallery-hero" style={{ 
        textAlign: 'center', 
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        marginBottom: '50px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            marginBottom: '20px',
            color: '#fff',
            fontWeight: '700'
          }}>
            {t('public.videos.title')}
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6'
          }}>
            {t('public.videos.description')}
          </p>
        </div>
      </div>

      <div style={{ 
        padding: '40px 20px', 
        maxWidth: '1200px', 
        margin: '0 auto' 
      }}>

        {/* Videos Count */}
        {videos.length > 0 && (
          <div style={{ 
            marginBottom: '30px',
            padding: '15px 20px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '1rem', 
              color: '#666',
              fontWeight: '500'
            }}>
              {t('public.videos.available', { count: videos.length })}
            </p>
          </div>
        )}

        {/* Videos Grid */}
        {videos.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 20px',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <i className="fas fa-video" style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3, color: '#999' }}></i>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>{t('public.videos.emptyTitle')}</h3>
            <p style={{ color: '#666' }}>{t('public.videos.emptyDescription')}</p>
          </div>
        ) : (
          <div className="videos-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '30px'
          }}>
          {videos.map((video) => {
            const thumbnailUrl = getThumbnailUrl(video.youtube_url, video.thumbnail_url || undefined);
            return (
              <div 
                key={video.id} 
                className="video-card" 
                onClick={() => handleVideoClick(video)}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#fff',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  overflow: 'hidden',
                  background: '#000'
                }}>
                  {thumbnailUrl ? (
                    <img 
                      src={thumbnailUrl} 
                      alt={video.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff',
                      fontSize: '48px'
                    }}>
                      <i className="fas fa-video"></i>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(255, 0, 0, 0.8)',
                    borderRadius: '50%',
                    width: '70px',
                    height: '70px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '28px',
                    transition: 'background 0.2s'
                  }}>
                    <i className="fas fa-play" style={{ marginLeft: '4px' }}></i>
                  </div>
                </div>
                <div style={{ padding: '20px' }}>
                  <h3 style={{ 
                    margin: '0 0 10px 0', 
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#333',
                    lineHeight: '1.4'
                  }}>
                    {video.title}
                  </h3>
                  {video.description && (
                    <p style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.5'
                    }}>
                      {video.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* Video Modal */}
      {showModal && selectedVideo && (
        <div 
          className="video-modal-overlay" 
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            className="video-modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '900px',
              background: '#000',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <button 
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              &times;
            </button>
            <div style={{ 
              position: 'relative', 
              paddingBottom: '56.25%', 
              height: 0, 
              overflow: 'hidden'
            }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
                src={getEmbedUrl(selectedVideo.youtube_url) || ''}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.title}
              />
            </div>
            <div style={{ 
              padding: '20px',
              background: '#fff',
              color: '#333'
            }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>
                {selectedVideo.title}
              </h3>
              {selectedVideo.description && (
                <p style={{ margin: 0, fontSize: '16px', color: '#666', lineHeight: '1.6' }}>
                  {selectedVideo.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoGallery;

